import { defineComponent, ref, reactive, computed, provide, watch, nextTick, toRef, unref, onMounted, PropType, toRefs } from 'vue';
import { FilterModel } from '@Apps/Search/Src/Models/FilterModel';
import ArrivalDeparturePicker from '@Components/ArrivalDeparturePicker/ArrivalDeparturePicker.vue';
import SelectionConfirmedEvent from '@Components/ArrivalDeparturePicker/Types/SelectionConfirmedEvent';
import LocationPicker2023 from '@Components/LocationPicker2023/LocationPicker2023.vue';
import CompanionsPicker from '@Components/CompanionsPicker/CompanionsPicker.vue';
import AmountPicker from '@Components/AmountPicker/AmountPicker.vue';
import { getLocationSelectionDisplay, getGuestsSelectionDisplay, getDatesSelectionDisplay, getActiveFilterCount } from '@Composables/FilterDisplayComposables';
import * as searchComposables from '@Composables/SearchComposables';
import searchBarTranslations from '@Components/SearchBar/Translations/VueTranslations';
import searchTranslations from '@Apps/Search/Translations/VueTranslations';
import SearchOrderingOption from '@Types/search/SearchOrderingOption';
import appTranslations from '@Components/CombinedFilter/Translations/VueTranslations';
import { FilterSelectionLocationEntry } from '../../Types/FilterSelections';
import * as locationPickerComposables from '@Components/LocationPicker2023/LocationPicker2023ClearLocationComposables';

export default defineComponent({

    components: { ArrivalDeparturePicker, LocationPicker2023, CompanionsPicker, AmountPicker },

    props: {

        filter: {
            type: Object as PropType<FilterModel>,
            required: true
        },

        forceHideContentTitle: Boolean,

        totalCount: Number,

      
    },

    emits: ["onChanged"],


    setup(props, context) {


        const root = ref<HTMLElement | null>(null);


        const filter = toRef(props, 'filter');
        const filterDefinitions = searchComposables.getFilterDefinitions();

        const landingPageId = ref(searchComposables.getSearchMetaDefinitions().landingPageId);
        const providerPageId = ref(searchComposables.getSearchMetaDefinitions().providerPageId);
        const isLandingPage = computed(() => landingPageId.value != null && landingPageId.value != "");
        const isProviderPage = computed(() => providerPageId != undefined);


        const contentTitle = ref(searchComposables.getSearchMetaDefinitions().contentTitle);

        const showContentTitle = computed(() => {

            return !props.forceHideContentTitle && (isLandingPage.value || isProviderPage.value) && contentTitle.value;
        });

     
        const showLocationPicker = ref(false);
        const showDatesPicker = ref(false);
        const showGuestsPicker = ref(false);

        const onLocationClicked = () => {

            showLocationPicker.value = !showLocationPicker.value;
        };
        const onDatesClicked = () => {

            showDatesPicker.value = !showDatesPicker.value;
        };

        const onGuestsClicked = () => {

            showGuestsPicker.value = !showGuestsPicker.value;
        };

        //setup date picker
        const onDatesSelected = (event: SelectionConfirmedEvent) => {

            filter.value.date.arrival = event.arrivalDateString;
            filter.value.date.departure = event.departureDateString;
            showDatesPicker.value = false;
        };

        const clearDateInput = () => {

            if (filter.value.date) {
                filter.value.date.arrival = "";
                filter.value.date.departure = "";
            }
        };

        const datesSelectionDisplay = computed(() => {

            return getDatesSelectionDisplay(filter.value);
        });




        const locationSelectionDisplay = computed(() => {

            return getLocationSelectionDisplay(filter.value);
        });


        const onLocationSelected = () => {


            showLocationPicker.value = false;

        }
        const clearLocationInput = () => {

            const determinedAction = locationPickerComposables.determineClearLocationFilterAction();

            if (determinedAction == locationPickerComposables.ClearLocationAction.ResetEntireFilter) {

                // this will clear the filter, and reload 
                clearLandingPageFilters();
                return;
            }

            filter.value.location.selection = new Array<FilterSelectionLocationEntry>();
        }

        const clearLandingPageFilters = () => {

            // for now the clear landingpage filters does the same thing as clearFilters, it will
            // clear everything.
            clearFilters();
        }

        const clearFilters = () => {

            searchComposables.clearFiltersAndRedirect();


        };

        const totalCriteriaCount = computed(() => {

            return getActiveFilterCount(filter.value);
        });

        const guestsSelectionDisplay = computed(() => {

            // for now we always show the number of guests with the "true" for has been active.
            // in the existing searchbar we had a property that was monitored if 'enabled',
            // so that on the homepage version we could show 'met wie' on first load, and only
            // change to guest counts once the 'popup' was opened for the first time.
            // in the current version we do not have a homepage equivalent, so for now
            // we will always show 2 persons by default, or the actual selection. 
            return getGuestsSelectionDisplay(filter.value, true);
        });


        const displaySortingMessage = computed(() =>

            (filter.value.orderBy == "11" || filter.value.orderBy == "12") && // eslint-disable-line eqeqeq
            (!filter.value.date.arrival || !filter.value.date.departure));


        const criteriaActions = {

            count(index: number) {

                return filter.value.criteria[index].length;
            },

            isChecked(index: number, val: number) {


                return filter.value.criteria[index].indexOf(val) > -1;

            },

            toggle(index: number, val: number) {


                const valueIndex = filter.value.criteria[index].indexOf(val);

                if (valueIndex > -1) {

                    filter.value.criteria[index].splice(valueIndex, 1);

                } else {

                    filter.value.criteria[index].push(val);
                }

            }
        }

        const onFocusArea = (area: string | undefined) => {

            showLocationPicker.value = area == "location";
            showDatesPicker.value = area == "dates";
            showGuestsPicker.value = area == "guests";

            if (root.value) {
                root.value.scrollTop = 0;
            }
            console.log("focus on area: " + area);
        };


        return {

            // refs
            root,

            // event actions external
            onFocusArea: onFocusArea,
            //--

            def: filterDefinitions,
            i18n: searchTranslations,
            contentTitle,
            showContentTitle,
            SearchOrderingOption,
            filter,
          
            
            onDatesSelected,
            clearDateInput,

            onLocationSelected,
            clearLocationInput,
            clearLandingPageFilters,
            clearFilters,

            showGuestsPicker,
            showDatesPicker,
            showLocationPicker,
            onLocationClicked,
            onDatesClicked,
            onGuestsClicked,

            datesSelectionDisplay,
            locationSelectionDisplay,
            guestsSelectionDisplay,

            totalCriteriaCount,

            criteriaActions,
            displaySortingMessage,

            resetFilters_Btn_Text: searchTranslations.searchApp_Label_ClearAllFilters,
            resetFilters_Btn_NoFilterText: searchTranslations.searchApp_Label_NoFilterText,
        };




    }

});

